import {
    InputCreateProduct,
    InputEditProduct,
    ProductExtendedProductUserExtended,
    ProductPermission,
    ProductStatus,
    WorkspaceType,
    useCreateProductMutation,
    useUpdateProductMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    Box,
    DescriptionTextField,
    Grid,
    IconAvatar,
    InfoCell,
    Stack,
    StickyFabPlacementHelper,
    SubmittingButton,
    TextField,
    ThumbnailAvatar,
    Typography,
} from "@biggeo/bg-ui/lab";
import { DatalabIcon, LinkOutline } from "@biggeo/bg-ui/lab/icons";
import { Formik } from "formik";
import * as O from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import { useState } from "react";
import Zod from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { CallBacksType } from "@biggeo/bg-common";
import {
    useErrorToast,
    useSuccessToast,
} from "../../toaster/containers/redux/hooks";

export type InputProductForm = {
    name: string;
    url?: string;
    spaceType: WorkspaceType;
    description?: string;
    fkOrganizationUserIds: string[];
};

export const CreateEditDatascapeContainer = ({
    productData,
    onContinue,
}: {
    readonly productData?: ProductExtendedProductUserExtended;
    readonly onContinue?: (data: ProductExtendedProductUserExtended) => void;
}) => {
    const successToast = useSuccessToast();
    const errorToast = useErrorToast();

    const [loading, setLoading] = useState(false);

    const setFormValues = (
        productData?: ProductExtendedProductUserExtended
    ) => {
        return {
            id: productData?.product.id,
            name: productData?.product.name || "",
            url: productData?.product.url || "",
            description: productData?.product.description || "",
            spaceType: productData?.product.spaceType || WorkspaceType.studio,
            fkOrganizationUserIds:
                compact(
                    productData?.productUserExtended?.map(
                        (productUser) => productUser.organizationUser?.id
                    )
                ) || [],
        };
    };

    const initValues = setFormValues(productData);

    const [snowflake, _setSnowflake] = useState(
        initValues.spaceType === WorkspaceType.snowflake
    );

    const { executeRemoteMutation: createProductMutation } =
        useCreateProductMutation();

    const { executeRemoteMutation: updateProductMutation } =
        useUpdateProductMutation();

    const handleCreateProduct = (
        input: InputCreateProduct,
        callbacks?: CallBacksType<ProductExtendedProductUserExtended>
    ) => {
        setLoading(true);
        createProductMutation({
            variables: { input },
            onCompleted: (data) => {
                setLoading(false);
                successToast("Product created successfully");
                callbacks?.onSuccess?.(data.createProduct);
            },
            onError: (err) => {
                setLoading(false);
                errorToast("Failed to create product");
                callbacks?.onError?.(err.message);
            },
        });
    };

    const handleEditProduct = (
        input: InputEditProduct,
        callbacks?: CallBacksType<ProductExtendedProductUserExtended>
    ) => {
        setLoading(true);
        updateProductMutation({
            variables: { input },
            onCompleted: (data) => {
                setLoading(false);
                successToast("Product updated successfully");
                callbacks?.onSuccess?.(data.updateProduct);
            },
            onError: (err) => {
                setLoading(false);
                errorToast("Failed to update product");
                callbacks?.onError?.(err.message);
            },
        });
    };

    const onSubmit = (
        product: InputProductForm,
        callbacks?: CallBacksType<ProductExtendedProductUserExtended>
    ) => {
        const { name, description, fkOrganizationUserIds, spaceType, url } =
            product;
        const productId = productData?.product.id;
        const productEnum = ProductPermission.datascapeAi;
        const status = ProductStatus.active;
        const image =
            "https://sabiggeomedia01.blob.core.windows.net/media/Background.png";

        pipe(
            productId,
            O.fromNullable,
            O.fold(
                () =>
                    handleCreateProduct(
                        {
                            name,
                            description,
                            fkOrganizationUserIds,
                            productEnum,
                            status,
                            image,
                            url,
                            spaceType,
                        },
                        callbacks
                    ),
                (id) =>
                    handleEditProduct(
                        {
                            id,
                            name,
                            description,
                            image,
                            fkOrganizationUserIds,
                            productEnum,
                            status,
                            url,
                            spaceType,
                        },
                        callbacks
                    )
            )
        );
    };

    const productName = "Datascape";

    return (
        <Formik
            initialValues={initValues}
            onSubmit={(v, actions) => {
                onSubmit(v, {
                    onSuccess: async (data) => {
                        actions.setSubmitting(false);
                        onContinue?.(data);
                    },
                });
            }}
            validationSchema={toFormikValidationSchema(
                Zod.object({
                    name: Zod.string(),
                    description: Zod.string().optional(),
                    url: Zod.string().optional(),
                    spaceType: Zod.nativeEnum(WorkspaceType),
                    fkOrganizationUserIds: Zod.array(Zod.string()).optional(), //ids of team members
                })
            )}
            validateOnMount
        >
            {({
                values,
                handleSubmit,
                setFieldValue,
                isValid,
                dirty,
                isSubmitting,
            }) => {
                return (
                    <Stack gap={4} width="100%">
                        <Grid item width="100%">
                            <Typography variant="title1" fontWeight="bold">
                                {productData
                                    ? "Manage Product"
                                    : `Create ${productName} space`}
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight="regular"
                                textColor="disabled.onContainer"
                            >
                                {productData
                                    ? "Edit product details here"
                                    : "Enter product space details here"}
                            </Typography>
                        </Grid>

                        <InfoCell
                            startNode={
                                <>
                                    <ThumbnailAvatar
                                        src={
                                            values.spaceType ===
                                            WorkspaceType.snowflake
                                                ? "https://sabiggeomedia01.blob.core.windows.net/media/sf.png"
                                                : "https://sabiggeomedia01.blob.core.windows.net/media/bg-background.png"
                                        }
                                        square
                                        size="sm"
                                    />
                                    <LinkOutline size="xs" />
                                    <Box
                                        sx={{
                                            border: (theme) =>
                                                `1px solid ${theme.palette.stroke[100]}`,
                                            borderRadius: (theme) =>
                                                theme.radius.default,
                                        }}
                                    >
                                        <IconAvatar size="sm">
                                            <DatalabIcon />
                                        </IconAvatar>
                                    </Box>
                                </>
                            }
                            subNode={
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    textColor="background.onMain"
                                >
                                    {productName}
                                </Typography>
                            }
                            density="none"
                        />

                        <TextField
                            fullWidth
                            required
                            name="name"
                            label="Name"
                            defaultValue={values.name || undefined}
                            placeholder="Enter name"
                            onChange={debounce(
                                (_, v) => setFieldValue("name", v),
                                1000
                            )}
                            disabled={isSubmitting}
                        />

                        <DescriptionTextField
                            name="description"
                            helperText={`Describe what this ${productName} space is for`}
                            value={values.description || undefined}
                            onChange={debounce((_e, v) => {
                                setFieldValue("description", v);
                            }, 1000)}
                            disabled={isSubmitting}
                        />
                        {snowflake && (
                            <Stack
                                gap={2}
                                sx={{
                                    padding: 4,
                                    borderRadius: (theme) => theme.radius.xs4,
                                    border: (theme) =>
                                        `1px solid ${theme.palette.outline}`,
                                }}
                                width="100%"
                            >
                                <ThumbnailAvatar
                                    square
                                    size="sm"
                                    src="https://sabiggeomedia01.blob.core.windows.net/media/sf.png"
                                />

                                <Grid item width="100%">
                                    <Typography
                                        variant="body3"
                                        fontWeight="semibold"
                                        textColor="background.onMain"
                                    >
                                        Native App URL
                                    </Typography>
                                    <Typography
                                        variant="body3"
                                        fontWeight="regular"
                                        textColor="disabled.onContainer"
                                    >
                                        Enter your {productName} Snowflake
                                        Instance URL. Don't have {productName}{" "}
                                        on Snowflake native apps yet?{" "}
                                        <Typography
                                            // TODO: add link here
                                            as="span"
                                            variant="body3"
                                            fontWeight="semibold"
                                            textColor="primary.main"
                                        >
                                            Launch native app
                                        </Typography>
                                    </Typography>
                                </Grid>

                                <TextField
                                    placeholder="https:// Placeholder"
                                    fullWidth
                                    name="snowflakeLink"
                                    label=""
                                    defaultValue={values.url || undefined}
                                    onChange={(_, v) => setFieldValue("url", v)}
                                    disabled={isSubmitting}
                                />
                            </Stack>
                        )}
                        <StickyFabPlacementHelper
                            sx={{
                                paddingY: 4,
                                backgroundColor: (theme) =>
                                    theme.palette.background.container,
                            }}
                        >
                            <SubmittingButton
                                loading={loading}
                                type={productData ? "saving" : "creating"}
                                disabled={!isValid || !dirty || isSubmitting}
                                onClick={() => handleSubmit()}
                            >
                                {productData ? "Save Changes" : "Continue"}
                            </SubmittingButton>
                        </StickyFabPlacementHelper>
                    </Stack>
                );
            }}
        </Formik>
    );
};
